import * as Y from 'yjs';
import { prosemirrorJSONToYDoc, yDocToProsemirrorJSON } from '@tiptap/y-tiptap';
import type { JSONContent } from '@tiptap/core';
import { extractFromDoc, extractTasks, nexusSchema } from '@nexus/editor';
import type { Store } from '@nexus/store';

/** Name of the Yjs XML fragment holding a page's document. */
export const FRAGMENT = 'default';

const COMPACT_AFTER = 100;

export const EMPTY_DOC: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };

/** No blocks, or a single empty paragraph. */
export const isEmptyDoc = (doc: JSONContent) =>
  !doc.content?.length ||
  (doc.content.length === 1 &&
    doc.content[0]!.type === 'paragraph' &&
    !doc.content[0]!.content?.length);

export function docToJSON(doc: Y.Doc): JSONContent {
  return yDocToProsemirrorJSON(doc, FRAGMENT) as JSONContent;
}

/** A Yjs update that creates `json` as page content (templates, import, restore). */
export function jsonToUpdate(json: JSONContent): Uint8Array {
  const doc = prosemirrorJSONToYDoc(nexusSchema(), json, FRAGMENT);
  const update = Y.encodeStateAsUpdate(doc);
  doc.destroy();
  return update;
}

export async function loadDoc(store: Store, pageId: string): Promise<Y.Doc> {
  const doc = new Y.Doc();
  const updates = await store.loadContent(pageId);
  Y.transact(doc, () => updates.forEach((u) => Y.applyUpdate(doc, u)), 'load');
  if (updates.length > COMPACT_AFTER) {
    await store.compactContent(pageId, Y.encodeStateAsUpdate(doc));
  }
  return doc;
}

export async function loadPageJSON(store: Store, pageId: string): Promise<JSONContent> {
  const doc = await loadDoc(store, pageId);
  const json = docToJSON(doc);
  doc.destroy();
  return json;
}

const indexQueue = new Map<string, Promise<void>>();

/**
 * Recompute a page's links, tags, tasks and search text. Calls for the same page
 * run in order, so an older index can never overwrite a newer one.
 */
export function reindexPage(store: Store, pageId: string, json: JSONContent): Promise<void> {
  const previous = indexQueue.get(pageId) ?? Promise.resolve();
  const next = previous.catch(() => {}).then(() => reindexNow(store, pageId, json));
  indexQueue.set(pageId, next);
  void next.finally(() => {
    if (indexQueue.get(pageId) === next) indexQueue.delete(pageId);
  });
  return next;
}

async function reindexNow(store: Store, pageId: string, json: JSONContent): Promise<void> {
  const { text, links, tags } = extractFromDoc(json);
  const resolved = new Map<string, string | null>();
  for (const l of links) {
    const key = l.target.toLowerCase();
    if (!l.pageId && !resolved.has(key)) {
      resolved.set(key, (await store.findPageByName(l.target))?.id ?? null);
    }
  }
  const props = await store.getProps(pageId);
  const propTags = Array.isArray(props.tags) ? props.tags.map(String) : [];
  await store.setPageIndex(pageId, {
    text,
    tags: [...tags, ...propTags.map((t) => t.replace(/^#/, ''))],
    links: links.map((l) => ({
      dstId: l.pageId ?? resolved.get(l.target.toLowerCase()) ?? null,
      dstTitle: l.target,
      kind: l.kind,
      context: l.context,
    })),
  });
  const tasks = [];
  for (const { linkTargets, ...t } of extractTasks(json)) {
    const named = [];
    for (const target of linkTargets) {
      const key = target.toLowerCase();
      if (!resolved.has(key)) resolved.set(key, (await store.findPageByName(target))?.id ?? null);
      const id = resolved.get(key);
      if (id) named.push(id);
    }
    tasks.push({ ...t, pageId, linkIds: [...t.linkIds, ...named] });
  }
  await store.setPageTasks(pageId, tasks);
}
